/**
 * Registration Service
 * 
 * Service for handling user account creation with the DIO API.
 */

import axios from 'axios';

export interface CreateAccountData {
  name: string;
  email: string;
  password: string;
  phone: string;
  turnstileToken?: string; // Optional for backward compatibility
}

export interface CreateAccountResponse {
  success: boolean;
  message?: string;
  data?: any;
}

class RegistrationService {
  private readonly apiUrl = `${process.env["DIO_API_URL"]}/register/`; // Using environment configuration
  private readonly schoolId = '0fc3eeed-a51d-4797-a381-f42b6ee5c03b';
  private readonly token = 'f3ffede12f130221726f838e657bf7d5b1bbe31a';

  /**
   * Validate user data before sending to API
   */
  private validateUserData(userData: CreateAccountData): boolean {
    if (!userData.name?.trim()) return false;
    if (!userData.email?.trim()) return false;
    if (!userData.phone?.trim()) return false;
    if (!userData.password?.trim()) return false;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userData.email.trim())) return false;
    
    // Basic phone validation (at least 10 digits)
    const phoneDigits = userData.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) return false;
    
    return true;
  }


  async createAccount(userData: CreateAccountData): Promise<CreateAccountResponse> {
    try {
      if (!this.validateUserData(userData)) {
        return {
          success: false,
          message: 'Dados inválidos fornecidos.'
        };
      }

      const formattedPhone = this.formatPhoneNumber(userData.phone);
      const turnstileToken = userData.turnstileToken || 'XXXX.DUMMY.TOKEN.XXXX';
      
      const formData = new URLSearchParams();
      formData.append('name', userData.name.trim());
      formData.append('email', userData.email.trim().toLowerCase());
      formData.append('password', userData.password);
      formData.append('phone', formattedPhone);
      formData.append('cf_turnstile_response', turnstileToken);
      formData.append('page_source', '/checkout/pro');
      formData.append('school', this.schoolId);

      const response = await axios.post(this.apiUrl, formData, {
        headers: {
          'authorization': 'Token',
          'school-id': this.schoolId,
          'token': this.token,
          'Content-Type': 'application/x-www-form-urlencoded',
          'origin': window.location.origin
        },
      });

      return {
        success: true,
        data: response.data,
        message: 'Conta criada com sucesso!'
      };
    } catch (error: any) {
      if (error.response) {
        // Server responded with error status
        const errorData = error.response.data || null;        
        return {
          success: false,
          message: this.getErrorMessage(error.response.status, errorData)
        };
      } else {
        // Network or other error
        return {
          success: false,
          message: 'Erro de conexão. Verifique sua internet e tente novamente.'
        };
      }
    }
  }

  private formatPhoneNumber(phone: string): string {
    const digitsOnly = phone.replace(/\D/g, '');

    let formattedPhone = digitsOnly;
    if (!formattedPhone.startsWith('55')) {
      formattedPhone = '55' + formattedPhone;
    }
    
    if (formattedPhone.length >= 11) {
      const countryCode = formattedPhone.substring(0, 2);
      const areaCode = formattedPhone.substring(2, 4);
      const firstPart = formattedPhone.substring(4, 8);
      const secondPart = formattedPhone.substring(8, 12);
      return `+${countryCode} ${areaCode} ${firstPart} ${secondPart}`;
    }
    
    return `+55 ${phone}`;
  }

  private getErrorMessage(status: number, errorData: any): string {
    switch (status) {
      case 400:
        if (errorData?.email) {
          return 'Este email já está em uso. Tente fazer login ou use outro email.';
        }
        if (errorData?.phone) {
          return 'Este número de telefone já está cadastrado.';
        }
        return 'Dados inválidos. Verifique as informações e tente novamente.';
      case 422:
        return 'Dados inválidos. Verifique se todos os campos estão preenchidos corretamente.';
      case 429:
        return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
      case 500:
        return 'Erro interno do servidor. Tente novamente em alguns minutos.';
      default:
        return 'Erro ao criar conta. Tente novamente.';
    }
  }
}

export const registrationService = new RegistrationService();
